import type { Rect } from '@view-model/models/common/basic/Rect';
import { D3Mouse } from '@view-model/models/common/d3/D3Mouse';
import { useD3RightClickCallback } from '@view-model/models/common/hooks/useD3RightClickCallback';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { type DragContext, DraggableSVGGElement } from './DraggableSVGGElement';

type Props = {
  rect: Rect;
  onDragStart: (e: React.MouseEvent<SVGGElement>) => void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
  onRightClick?: (position: { x: number; y: number }) => void;
};

export const MultiSelectionRect: React.FC<Props> = ({ rect, onDrag, onDragStart, onDragEnd, onRightClick }: Props) => {
  const { position, width, height } = rect;
  const myRef = useRef<SVGGElement>(null);
  const rectRef = useRef<SVGRectElement>(null);
  const d3Mouse = useMemo(() => new D3Mouse(myRef), []);
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout | null>(null);

  useD3RightClickCallback(
    myRef,
    useCallback(
      (event: MouseEvent) => {
        const [x, y] = d3Mouse.getPosition(event);
        if (onRightClick) onRightClick({ x, y });
      },
      [d3Mouse, onRightClick]
    ),
    // 本来はハンドルする場合だけ伝搬を止めればいいが、現状ではズームが発動するのでpreventDefaultも止める
    true
  );

  // 左クリックイベントは複数選択矩形の背後にある要素に伝達する
  const handleClick = (event: React.MouseEvent<SVGGElement>) => {
    // 背後要素への疑似クリックを発火させるため、元イベントの伝搬とデフォルト動作はここで止める
    event.stopPropagation();
    event.preventDefault();

    const rectElement = rectRef.current;
    if (!rectElement) {
      return;
    }

    rectElement.style.pointerEvents = 'none';

    const timeoutId = setTimeout(() => {
      const elementBelow = document.elementFromPoint(event.clientX, event.clientY);
      // elementFromPoint の結果にかかわらず pointerEvents は必ず元に戻す
      rectElement.style.pointerEvents = 'auto';

      if (!elementBelow) return;

      const clickEvent = new MouseEvent('click', {
        clientX: event.clientX,
        clientY: event.clientY,
        bubbles: true,
        shiftKey: event.shiftKey,
        ctrlKey: event.ctrlKey,
        metaKey: event.metaKey,
      });
      elementBelow.dispatchEvent(clickEvent);
    }, 0);

    setTimeoutId(timeoutId);
  };

  useEffect(() => {
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [timeoutId]);

  return (
    <g ref={myRef}>
      <DraggableSVGGElement position={position} onDragStart={onDragStart} onDrag={onDrag} onDragEnd={onDragEnd}>
        <rect
          ref={rectRef}
          className="stroke-gray-600"
          fillOpacity={0}
          strokeWidth={3}
          cursor="move"
          width={width}
          height={height}
          onClick={handleClick}
          rx={16}
          ry={16}
        />
      </DraggableSVGGElement>
    </g>
  );
};
